import { useCallback, useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';
import { useLazyQuery } from '@apollo/client';
import { StyledLeftTooltip } from 'modules/workflowNodeEditors/EditorBody.styled';
import { CreateListButton } from 'modules/workflowNodeEditors/editorComponents/CreateListButton';
import { TakeoverType } from 'modules/workflows/components/takeovers';
import { SearchCombo, StartNodeData, emptySearchQuery, startUtils } from 'modules/workflows';
import {
  closeTakeover,
  getIsJourneyStartSuppressionEnabled,
  openTakeover,
  getIsJourneysStartTilePaginatedSelectEnabled,
  getIsJourneysStartTileFilterSuppression,
  getIsJourneyStartTileGetEstimatedUserEnteringCountEnabled,
} from 'modules/workflows/ducks';
import { CounterBadge } from 'ui/badge';
import { IconButton, BUTTON_VARIANTS } from 'ui/button';
import { ErrorText } from 'ui/formField';
import { Add16, Delete24 } from 'ui/icons';
import { Subtitle2 } from 'ui/theme';
import { LIST_TYPES } from 'constants/listTypes';
import { useStartNodeContext } from '../StartNodeEditor.context';
import { useGetEntrySourceMetadata, useHandleResourceValueChange } from '../StartNodeEditor.hooks';
import { EntrySourceComponentProps, InternalOnChangeProp } from '../StartNodeEditor.types';
import { ResourceListSelect } from '../resourceComponents';
import { ViewListLink } from '../resourceComponents/ViewListLink';
import {
  DefineAudienceSelectWrapper,
  MiniSubtitle2,
  StyledListSelect,
  StyledListSelectV2,
  StyledSubtitle2,
  SuppressListWrapper,
  CriteriaButton,
  CriteriaWrapper,
} from './DefineAudienceSelect.styles';
import { getFoundLists } from './DefineAudienceSelect.utils';
import {
  DEFINE_AUDIENCE_READ_ONLY_TOOLTIP,
  DEFINE_AUDIENCE_SELECT_ID,
  DEFINE_AUDIENCE_SELECT_VIEW_LIST_LINK,
  DEFINE_AUDIENCE_TRIGGER_SELECT,
  DEFINE_CRITERIA_BUTTON,
  DEFINE_CRITERIA_BUTTON_COUNTER_BADGE,
  DEFINE_CRITERIA_DELETE_BUTTON,
} from './ScheduleEntrySource.constants';
import { DEFAULT_LIST_TYPES, parsePaginatedListItem, fetchListsQuery } from '../../formElements';
import { UserListCount } from './UserListCount';

export const DefineAudienceSelect = ({
  displayValue = '',
  hasCloningError = false,
}: EntrySourceComponentProps) => {
  const dispatch = useDispatch();
  const isJourneyStartSuppressionEnabled = useSelector(getIsJourneyStartSuppressionEnabled);
  const isJourneysStartTileFilterSuppressionEnabled = useSelector(
    getIsJourneysStartTileFilterSuppression,
  );
  const isJourneysStartTilePaginatedSelectEnabled = useSelector(
    getIsJourneysStartTilePaginatedSelectEnabled,
  );
  const isJourneyStartTileGetEstimatedUserEnteringCountEnabled = useSelector(
    getIsJourneyStartTileGetEstimatedUserEnteringCountEnabled,
  );
  const { isReadOnly, nodeData, setNodeData } = useStartNodeContext();
  const entrySourceMetadata = useGetEntrySourceMetadata();
  const { selectPlaceholder, resourceFieldValue: value } = entrySourceMetadata;
  const showLink = !!value && startUtils.isResourceValueValid(value);
  const [tooltipContent, setTooltipContent] = useState<React.ReactNode>();
  const [suppressionListErrorMsg, setSuppressionListErrorMsg] = useState<string | undefined>();
  const suppressionListIds = useMemo(() => {
    if (!nodeData?.suppressionListIds) return [];
    return (nodeData?.suppressionListIds || []).map((id) => `${id}`);
  }, [nodeData]);

  const handleChange = useHandleResourceValueChange();

  const handleListSelect = (opt: InternalOnChangeProp) => {
    const { value: selectedValue } = opt;

    /**
     * After list creation, the select incorrectly triggers an `onChange` event
     * since the incoming listId has changed.  In certain conditions, this
     * causes the select name to be incorrect.
     * Checking for the id prevents that from happening.
     */
    if (selectedValue !== value) {
      handleChange(opt);
    }
  };

  const handleSuppressListSelect = (ids: string[]) => {
    if (suppressionListErrorMsg && !isEqual(suppressionListIds, ids)) {
      setSuppressionListErrorMsg(undefined);
    }
    setNodeData({ suppressionListIds: ids.map((id) => parseInt(id, 10)) });
  };

  const handleSelectedDataFetch = (data: unknown) => {
    const lists = (data as any)?.lists?.results || [];

    if (lists.length !== suppressionListIds.length) {
      const { found, notFound } = getFoundLists(suppressionListIds, lists);
      handleSuppressListSelect(found);
      setSuppressionListErrorMsg(`Some lists could not be found: ${notFound.join(', ')}`);
    }
  };

  const handleSearchQuerySelect = (criteria?: { searchQuery: SearchCombo }) => {
    const emptyCriteria = JSON.stringify(emptySearchQuery);
    if (JSON.stringify(criteria?.searchQuery) === emptyCriteria || !criteria) {
      setNodeData({ searchCombo: undefined });
    } else {
      setNodeData({ searchCombo: criteria?.searchQuery });
    }
  };

  const SubtitleCmp = isJourneyStartSuppressionEnabled ? StyledSubtitle2 : Subtitle2;

  const excludeOptionsParser = useCallback(
    (list: any) => {
      const item = parsePaginatedListItem(list);
      // Disable the option if it the selected list
      const isOptionDisabled = (item.id as unknown as number) === nodeData.emailListId;
      const additionalProps = isOptionDisabled
        ? {
            isDisabled: isOptionDisabled,
            tooltipContent: 'Already selected under Add to journey',
          }
        : {};

      return { ...item, ...additionalProps };
    },
    [nodeData.emailListId],
  );

  const sq = nodeData.searchCombo?.searchQueries || [];

  const [fetchSelectedData] = useLazyQuery(fetchListsQuery, {
    fetchPolicy: 'cache-and-network',
    onCompleted: handleSelectedDataFetch,
    variables: { ids: suppressionListIds },
  });

  useEffect(() => {
    if (
      suppressionListIds.length &&
      !isJourneysStartTilePaginatedSelectEnabled &&
      isJourneyStartSuppressionEnabled
    ) {
      fetchSelectedData();
    }
  }, []);

  return (
    <div>
      {isJourneyStartTileGetEstimatedUserEnteringCountEnabled ? (
        <UserListCount />
      ) : (
        <SubtitleCmp>Audience</SubtitleCmp>
      )}
      {isJourneyStartSuppressionEnabled && <MiniSubtitle2>Add to journey</MiniSubtitle2>}

      <StyledLeftTooltip
        data-test={DEFINE_AUDIENCE_READ_ONLY_TOOLTIP}
        tooltipContent={tooltipContent}
        isEnabled={isReadOnly && !!tooltipContent}
      >
        <DefineAudienceSelectWrapper>
          <ResourceListSelect
            data-test={DEFINE_AUDIENCE_TRIGGER_SELECT}
            id={DEFINE_AUDIENCE_SELECT_ID}
            placeholder={displayValue || selectPlaceholder}
            isError={hasCloningError && !startUtils.isResourceValueValid(value)}
            nodeData={nodeData ?? ({} as unknown as StartNodeData)}
            {...entrySourceMetadata}
            value={value}
            onChange={handleListSelect}
            style={{ wordBreak: 'break-all' }}
            isDisabled={isReadOnly}
            setTooltipContent={setTooltipContent}
            {...(isJourneyStartSuppressionEnabled && {
              parseOption: (list: any) => {
                const item = parsePaginatedListItem(list);
                // Disable the option if it is in the suppression list
                const isOptionDisabled = suppressionListIds.includes(`${item.id}`);
                const additionalProps = isOptionDisabled
                  ? {
                      isDisabled: isOptionDisabled,
                      tooltipContent: 'Already selected under Exclude from journey',
                    }
                  : {};

                return { ...item, ...additionalProps };
              },
            })}
          />
          {!isReadOnly && <CreateListButton onListCreate={setNodeData} showIcon />}
        </DefineAudienceSelectWrapper>
      </StyledLeftTooltip>
      {showLink && <ViewListLink id={DEFINE_AUDIENCE_SELECT_VIEW_LIST_LINK} value={value} />}
      {isJourneyStartSuppressionEnabled && (
        <>
          <MiniSubtitle2>Exclude from journey</MiniSubtitle2>
          <SuppressListWrapper>
            {isJourneysStartTilePaginatedSelectEnabled ? (
              <StyledListSelectV2
                listType={[...DEFAULT_LIST_TYPES, LIST_TYPES.SUPPRESSION]}
                isMulti
                isDisabled={isReadOnly}
                // TODO: Fix the types in PaginatedSelect to include an array
                // @ts-ignore
                value={suppressionListIds || []}
                onChange={handleSuppressListSelect}
                parseOption={excludeOptionsParser}
                errorMsg={suppressionListErrorMsg}
                fetchSelectedItemsOptions={{
                  onCompleted: handleSelectedDataFetch,
                }}
              />
            ) : (
              <>
                <StyledListSelect
                  listType={[...DEFAULT_LIST_TYPES, LIST_TYPES.SUPPRESSION]}
                  isMulti
                  isDisabled={isReadOnly}
                  // TODO: Fix the types in PaginatedSelect to include an array
                  // @ts-ignore
                  value={suppressionListIds || []}
                  onChange={handleSuppressListSelect}
                  parseOption={excludeOptionsParser}
                  isError={!!suppressionListErrorMsg}
                />
                {suppressionListErrorMsg && <ErrorText>{suppressionListErrorMsg}</ErrorText>}
              </>
            )}
          </SuppressListWrapper>
        </>
      )}
      {isJourneysStartTileFilterSuppressionEnabled && (
        <>
          <MiniSubtitle2>Additional entry requirements</MiniSubtitle2>
          <CriteriaWrapper>
            <CriteriaButton
              isDisabled={false}
              data-test={DEFINE_CRITERIA_BUTTON}
              onClick={() =>
                dispatch(
                  openTakeover(TakeoverType.ScheduledJourneyAdditionalCriteria, {
                    nodeData,
                    onChange: handleSearchQuerySelect,
                    onClose: () =>
                      dispatch(closeTakeover(TakeoverType.ScheduledJourneyAdditionalCriteria)),
                  }),
                )
              }
            >
              {sq.length > 0 ? (
                <>
                  {'Criteria'}
                  <CounterBadge data-test={DEFINE_CRITERIA_BUTTON_COUNTER_BADGE}>
                    {' '}
                    {sq.length}
                  </CounterBadge>
                </>
              ) : (
                <>
                  <Add16 />
                  {`Add criteria`}
                </>
              )}
            </CriteriaButton>
            {sq.length > 0 && (
              <IconButton
                data-test={DEFINE_CRITERIA_DELETE_BUTTON}
                variant={BUTTON_VARIANTS.SECONDARY}
                onClick={() => handleSearchQuerySelect()}
              >
                <Delete24 />
              </IconButton>
            )}
          </CriteriaWrapper>
        </>
      )}
    </div>
  );
};
